import { useEffect, useRef, useState } from "react";

interface UsePriceGradientProps {
  currentPrice: number | null;
  defaultClassName?: string;
}

interface UsePriceGradientResult {
  gradientClassName: string;
  isFlashing: boolean;
}

export const usePriceGradient = ({
  currentPrice,
  defaultClassName = "text-content_primary",
}: UsePriceGradientProps): UsePriceGradientResult => {
  const lastPriceRef = useRef<number | null>(null);
  const [isFlashing, setIsFlashing] = useState(false);
  const [priceChange, setPriceChange] = useState<number>(0);
  const flashTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // 价格变化检测逻辑
  useEffect(() => {
    // 检查价格有效性
    const isValidPrice = (price: number | null): price is number => {
      return (
        price !== null && price !== undefined && !isNaN(price) && price >= 0
      );
    };

    // 如果当前价格无效，直接返回不做任何处理
    if (!isValidPrice(currentPrice)) {
      return;
    }

    const current = currentPrice;
    const previous = lastPriceRef.current;

    // 只有当前后两个价格都有效时才进行比较
    if (isValidPrice(previous) && current !== previous) {
      const change = current - previous;
      const changeRate = Math.abs(change) / previous;

      setPriceChange(change);

      // 变化率超过 0.01% 才触发闪烁
      if (changeRate > 0.0001) {
        setIsFlashing(true);
        clearTimeout(flashTimeoutRef.current);
        flashTimeoutRef.current = setTimeout(() => {
          setIsFlashing(false);
        }, 3000);
      }
    }
    // 只有有效价格才更新 lastPriceRef
    lastPriceRef.current = current;
  }, [currentPrice]);

  // 清理定时器
  useEffect(() => {
    return () => {
      clearTimeout(flashTimeoutRef.current);
    };
  }, []);

  // 计算渐变类名
  const getGradientClassName = (): string => {
    if (isFlashing) {
      return priceChange > 0 ? "price-gradient-up" : "price-gradient-down";
    }
    return defaultClassName;
  };

  return {
    gradientClassName: getGradientClassName(),
    isFlashing,
  };
};
